// src/Components/Auth/Login.tsx
import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { COLORS } from '../../Styles/Colors';
// Add import for your logo image (adjust the path as needed)
import logo from '../../assets/images/AIMS_Logo_color_TM.png'; // Example path - update this to your actual logo location
import Cookies from 'js-cookie';
import apiClient from '../../Services/ApiClient';
import MsalConfig from './Msal';

import { 
  Box, 
  Divider, 
  Typography 
} from '@mui/material';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(135deg, ${COLORS.lightGray} 0%, #e0e0e0 100%);
  padding: 16px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const LoginForm = styled.form`
  background: white;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 420px;
  text-align: center;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 1.5rem 1.25rem;
  }
`;

// Modify Title to be a div instead of h2 since it will contain an image
const Title = styled.div`
  margin-bottom: 1.5rem;
`;

// Add a styled component for the logo
const Logo = styled.img`
  max-width: 250px;
  height: auto;

  @media (max-width: 600px) {
    max-width: 180px;
  }
`;

// Rest of your styled components remain the same
const Input = styled.input`
  width: 100%;
  padding: 0.9rem;
  margin: 0.75rem 0;
  border: 1px solid ${COLORS.gray};
  border-radius: 6px;
  box-sizing: border-box;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  &:focus {
    border-color: ${COLORS.teal};
    outline: none;
    box-shadow: 0 0 4px rgba(0, 128, 128, 0.3);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.9rem;
  margin-top: 1.5rem;
  background-color: ${COLORS.teal};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 500;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: ${COLORS.darkTeal};
  }
  &:disabled {
    background-color: ${COLORS.gray};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: ${COLORS.red};
  margin: 0.75rem 0;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ForgotPasswordLink = styled.a`
  display: block;
  margin-top: 1rem;
  color: ${COLORS.teal};
  font-size: 0.9rem;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: ${COLORS.darkTeal};
  }
`;

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [forgotEmail, setForgotEmail] = useState<string>('');
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email || !password) {
      setError('Please enter both email and password');
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiClient('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }


      Cookies.set('token', data.token, { expires: 1, sameSite: 'lax' });

      navigate('/home');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await apiClient('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset link');
      }

      setError('Reset link sent! Check your email.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };


  const toggleShowPassword = () => {
    setError('');
    setShowForgotPassword(!showForgotPassword);
  }

  return (
    <LoginContainer>
      {!showForgotPassword ? (
        <LoginForm onSubmit={handleSubmit}>
          <Title>
            <Logo src={logo} alt="Company Logo" />
          </Title>
          <Input
            type="text"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
        <Divider sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
          or continue with
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>



          <MsalConfig />
          <ForgotPasswordLink onClick={toggleShowPassword}>
            Forgot Password?
          </ForgotPasswordLink>
        </LoginForm>
      ) : (
        <LoginForm onSubmit={handleForgotPassword}>
        <Title>
            <Logo src={logo} alt="Company Logo" />
          </Title>
         
          <Input
            type="email"
            placeholder="Enter your email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
          <ForgotPasswordLink onClick={toggleShowPassword}>
            Back to Sign In
          </ForgotPasswordLink>
        </LoginForm>
      )}
    </LoginContainer>
  );
};

export default Login;